import PageHeader from "../../components/layout/PageHeader.jsx";
import { renderToStaticMarkup } from 'react-dom/server';
import useSettings from "../../hooks/Settings";
import { client } from "../../hooks/Client";
import Location from "../../hooks/Location";
import { db } from "../../firebase/config";
import Hostname from '../../hooks/Hostname'
import { useState, useEffect, useRef } from "react";
import { useTranslation } from '../../StateManagment/TranslationContext';
import ReadOnlyGuard from "../../components/common/ReadOnlyGuard";

const COOLDOWN_SECONDS = 5;

const EmailQuestionaire = () => {
  // Translation context
  const { translations, setTranslationInput } = useTranslation();
  const lastSentRef = useRef({ lang: null, keysHash: null });

  // State
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // Hooks
  const companyName = useSettings().compagnyName;
  const loc = Location();
  const questionnaireId = loc[3];
  const measureMomentID = loc[4];
  const research = loc[5];
  const persona = loc[6];
  const languageCode = loc[8];
  const host = Hostname();

  // Minimal strings for translation
  useEffect(() => {
    if (!languageCode) return;

    if (languageCode === 'nl') {
      const nlHash = 'nl-empty';
      if (lastSentRef.current.lang === 'nl' && lastSentRef.current.keysHash === nlHash) return;
      setTranslationInput({ strings: {}, language: languageCode });
      lastSentRef.current = { lang: 'nl', keysHash: nlHash };
      return;
    }

    const base = {
      "Ik ga met je mee terwijl je de vragen invult. Benieuwd?": "I'll guide you while you fill in the questions. Curious?",
      "Hi,": "Hi,",
      "Groet,": "Regards,",
      "Team": "Team",
      "Alle rechten voorbehouden.": "All rights reserved.",
      "Open Vragenlijst": "Open Questionnaire",
      "We doen onderzoek naar hoe mensen het invullen van vragenlijsten ervaren.": "We are researching how people experience filling out questionnaires.",
      "Met jouw antwoorden kunnen we leren hoe we vragenlijsten leuker kunnen maken.": "With your answers, we can learn how to make questionnaires more enjoyable.",
      "Daarom krijg je nu een vragenlijst die nét even anders is dan normaal.": "That's why you’ll receive a questionnaire that’s a bit different than usual.",
      "Belangrijk om te weten:": "Good to know:",
      "Er is geen goed of fout antwoord - het gaat om jouw ervaring": "There are no right or wrong answers — it’s about your experience.",
      "Jouw antwoorden zijn anoniem": "Your answers are anonymous.",
      "Het invullen duurt ongeveer 10-15 minuten": "It takes about 10–15 minutes to complete.",
      "Alvast bedankt voor je deelname!": "Thanks in advance for your participation!",
    };

    const keysHash = `${languageCode}|${Object.keys(base).length}`;
    if (lastSentRef.current.lang === languageCode && lastSentRef.current.keysHash === keysHash) return;
    setTranslationInput({ strings: base, language: languageCode });
    lastSentRef.current = { lang: languageCode, keysHash };
  }, [languageCode, setTranslationInput]);

  // Helper
  const t = (key, fallback) => (translations?.[key] ?? fallback ?? key);

  // Email template
  const EmailTemplate = ({ companyName, actionText = "Open Vragenlijst" }) => {
    const deccaMessage = t(
      "Ik ga met je mee terwijl je de vragen invult. Benieuwd?",
      "Ik ga met je mee terwijl je de vragen invult. Benieuwd?"
    );

    const actionUrl = `https://app.deccos.nl/${client}/questionnaireopen/${questionnaireId}/${measureMomentID}/${research}/${persona}/open/${languageCode || "nl"}`;
    const bgUrl =
      "https://firebasestorage.googleapis.com/v0/b/deccos-app.appspot.com/o/emailDeccaDesign.png?alt=media&token=c15cf95b-a3c1-4efe-a256-b6565fc94fb6";

    return (
      <html>
        <body style={{ margin: 0, padding: 0, fontFamily: "Arial, sans-serif", backgroundColor: "#ffffff" }}>
          <table width="100%" cellPadding="0" cellSpacing="0" border="0">
            <tbody>
              <tr>
                <td align="center">
                  <table
                    width="600"
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    style={{ margin: "20px auto", backgroundColor: "#ffffff" }}
                  >
                    <tr>
                      <td
                        width="600"
                        height="300"
                        background={bgUrl}
                        style={{
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          textAlign: "center",
                          verticalAlign: "middle",
                        }}
                        align="center"
                        valign="middle"
                      >
                        <table width="100%" cellPadding="0" cellSpacing="0" border="0">
                          <tbody>
                            <tr>
                              <td align="center" style={{ padding: "20px" }}>
                                <table
                                  cellPadding="0"
                                  cellSpacing="0"
                                  border="0"
                                  style={{
                                    display: "inline-block",
                                    backgroundColor: "rgba(255,255,255,0.9)",
                                    padding: "10px 20px",
                                    borderRadius: "20px",
                                    fontSize: "20px",
                                    color: "#333333",
                                    lineHeight: "1.2",
                                  }}
                                >
                                  <tbody>
                                    <tr>
                                      <td align="center">{deccaMessage}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>

                    <tr>
                      <td style={{ padding: "20px" }}>
                        <p style={{ margin: "0 0 10px" }}>{t("Hi,", "Hi,")}</p>

                        <p style={{ margin: "0 0 10px" }}>
                          {t("We doen onderzoek naar hoe mensen het invullen van vragenlijsten ervaren.",
                             "We doen onderzoek naar hoe mensen het invullen van vragenlijsten ervaren.")}
                        </p>
                        <p style={{ margin: "0 0 10px" }}>
                          {t("Met jouw antwoorden kunnen we leren hoe we vragenlijsten leuker kunnen maken.",
                             "Met jouw antwoorden kunnen we leren hoe we vragenlijsten leuker kunnen maken.")}
                        </p>
                        <p style={{ margin: "0 0 16px" }}>
                          {t("Daarom krijg je nu een vragenlijst die nét even anders is dan normaal.",
                             "Daarom krijg je nu een vragenlijst die nét even anders is dan normaal.")}
                        </p>

                        <p style={{ margin: "0 0 6px", fontWeight: "bold" }}>
                          {t("Belangrijk om te weten:", "Belangrijk om te weten:")}
                        </p>
                        <ul style={{ margin: "0 0 16px 18px", padding: 0 }}>
                          <li style={{ marginBottom: 6 }}>
                            {t("Er is geen goed of fout antwoord - het gaat om jouw ervaring",
                               "Er is geen goed of fout antwoord - het gaat om jouw ervaring")}
                          </li>
                          <li style={{ marginBottom: 6 }}>
                            {t("Jouw antwoorden zijn anoniem",
                               "Jouw antwoorden zijn anoniem")}
                          </li>
                          <li style={{ marginBottom: 6 }}>
                            {t("Het invullen duurt ongeveer 10-15 minuten",
                               "Het invullen duurt ongeveer 10-15 minuten")}
                          </li>
                        </ul>

                        <p style={{ textAlign: "center", margin: "0 0 20px" }}>
                          <a
                            href={actionUrl}
                            style={{
                              display: "inline-block",
                              padding: "12px 24px",
                              backgroundColor: "#28a745",
                              color: "#ffffff",
                              textDecoration: "none",
                              borderRadius: "4px",
                              fontWeight: "bold",
                            }}
                          >
                            {t(actionText, actionText)}
                          </a>
                        </p>

                        <p style={{ margin: "0 0 16px" }}>
                          {t("Alvast bedankt voor je deelname!", "Alvast bedankt voor je deelname!")}
                        </p>

                        <p style={{ margin: 0 }}>
                          {t("Groet,", "Groet,")}
                        </p>
                        <p style={{ margin: 0 }}>
                          {t("Team", "Team")} {companyName}
                        </p>
                      </td>
                    </tr>

                    <tr>
                      <td
                        align="center"
                        style={{ padding: "20px", fontSize: "12px", color: "#777777" }}
                      >
                        &copy; {new Date().getFullYear()} {companyName}.{" "}
                        {t("Alle rechten voorbehouden.", "Alle rechten voorbehouden.")}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    );
  };

  const renderEmail = () =>
    renderToStaticMarkup(<EmailTemplate companyName={companyName} actionText="Open Vragenlijst" />);

  // Cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const isDisabled = sending || cooldown > 0;
  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");

  // Send Email
  const sendEmail = async () => {
    setError("");
    setSent(false);
    if (!validateEmail(email)) {
      setError("Voer een geldig e-mailadres in.");
      return;
    }
    if (isDisabled) return;

    try {
      setSending(true);
      await db.collection("Email").doc().set({
        to: email,
        from: "info@deccos.nl",
        replyTo: `${host.Name}`,
        cc: "info@Deccos.nl",
        message: {
          subject: `Ga je mee op pad?`,
          html: renderEmail(),
          Emailadres: "QuestionnaireEmail",
          Type: "Verification mail",
        },
      });
      setSent(true);
      setEmail("");
      setCooldown(COOLDOWN_SECONDS);
    } catch (error) {
      console.error("Error sending email:", error);
      setError("Versturen mislukt. Probeer het opnieuw.");
    } finally {
      setSending(false);
    }
  };

  const onEmailChange = (e) => {
    setEmail(e.target.value);
    if (sent) setSent(false);
    if (error) setError("");
  };

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader
          heading="Vragenlijst emailen"
          description="Hier kun je de vragenlijst per email versturen naar de deelnemers."
          AI={false}
        />

        <div className="dashboard-container">
          <EmailTemplate companyName={companyName} actionText="Open Vragenlijst" />
        </div>

        <ReadOnlyGuard edit="measurement.edit">
        <div className="dashboard-container" style={{ maxWidth: 600 }}>
          <label style={{ display: "block", marginBottom: 8 }}>E-mailadres</label>
          <input
            type="email"
            value={email}
            placeholder={cooldown > 0 ? `Even wachten (${cooldown}s)` : "naam@voorbeeld.nl"}
            onChange={onEmailChange}
            disabled={isDisabled}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 6,
              border: "1px solid #ccc",
              marginBottom: 8,
              opacity: isDisabled ? 0.7 : 1,
            }}
          />
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button
              onClick={sendEmail}
              disabled={isDisabled}
              style={{
                padding: "10px 16px",
                borderRadius: 6,
                border: "none",
                background: isDisabled ? "#8cbf96" : "#28a745",
                color: "white",
                cursor: isDisabled ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {sending ? "Versturen…" : cooldown > 0 ? `Wachten (${cooldown}s)` : "Stuur Email"}
            </button>

            {sent && <span style={{ color: "#2e7d32", fontWeight: 600 }}>E-mail verzonden!</span>}
            {error && <span style={{ color: "#b00020", fontWeight: 600 }}>{error}</span>}
          </div>
        </div>
        </ReadOnlyGuard>
      </div>
    </div>
  );
};

export default EmailQuestionaire;



